import { scrollToElement } from 'common/utils/scroll';
import debounce from 'lodash/debounce';
import { Omit } from 'projects/types';
import * as React from 'react';
import Field from './Field';

interface AttachProps<T> {
    name: string;
    value: T;
    onChange: (val: T) => void;
}

export type FormErrors = { [prop: string]: string };
export type Milliseconds = number;

interface Options<T> {
    onSave?: (changed: Partial<T>) => Promise<FormErrors>;
    autosaveDelay?: Milliseconds;
}

export type Observer = () => Promise<void>;

export class ObservableForm<T> {
    private data: T;
    private changes: Partial<T> = {};
    private saving: Partial<T> = {};
    private options: Options<T>;
    private errors: FormErrors = {};
    private observers: Map<keyof T, Observer[]>;
    private saveInProgress: Promise<FormErrors> | undefined;
    private autosave = () => {};

    constructor(data: T, options: Options<T> = {}) {
        this.data = data;
        this.options = options;
        this.observers = new Map();
        if (options.autosaveDelay) {
            this.autosave = debounce(() => this.save(true), options.autosaveDelay);
        }
    }

    setData(data: T) {
        this.data = data;
    }

    attach<K extends keyof T>(name: K): AttachProps<T[K]> {
        return {
            name: name as string,
            value: this.get(name),
            onChange: (val: T[K]) => this.set(name, val),
        };
    }

    get<K extends keyof T>(name: K): T[K] {
        if (this.changes[name] !== undefined) {
            return (this.changes as T)[name];
        }
        if (this.saving[name] !== undefined) {
            return (this.saving as T)[name];
        }

        return this.data[name];
    }

    getError<K extends keyof T>(name: K): string {
        return this.errors[name as string];
    }

    hasErrors(): boolean {
        return Object.keys(this.errors).length > 0;
    }

    hasChanges(): boolean {
        return Object.keys(this.changes).length > 0;
    }

    firstError(): keyof T | null {
        if (!this.hasErrors()) {
            return null;
        }
        return Object.keys(this.errors)[0] as keyof T;
    }

    // Ignore "__typename" attribute from generated Apollo types
    set<K extends keyof T>(name: K, val: T[K] | Omit<T[K], '__typename'>) {
        this.changes[name] = val as T[K];
        this.autosave();
        return this.repaint(name);
    }

    scrollTo<K extends keyof T>(name: K) {
        const element = document.getElementById(`field-${name}`);
        if (!element) {
            return;
        }

        scrollToElement(element, 0, 300);
    }

    repaint(name: keyof T) {
        const observers = this.observers.get(name) || [];
        const promises = observers.map(o => o());
        return Promise.all(promises);
    }

    repaintAll() {
        const repaints: Promise<void>[] = [];
        this.observers.forEach(observers =>
            observers.forEach(observer => repaints.push(observer()))
        );
        return Promise.all(repaints);
    }

    listen(name: keyof T, observer: Observer) {
        const keyObservers = this.observers.get(name) || [];
        keyObservers.push(observer);
        this.observers.set(name, keyObservers);
    }

    unlisten(name: keyof T, observer: Observer) {
        let keyObservers = this.observers.get(name) || [];
        keyObservers = keyObservers.filter(v => v !== observer);
        this.observers.set(name, keyObservers);
    }

    async save(autosave = false) {
        if (!this.options.onSave) {
            throw new Error('onSave must be set in options to use save');
        }
        if (this.saveInProgress !== undefined) {
            return await this.saveInProgress;
        }

        this.saveInProgress = new Promise<FormErrors>((resolve, reject) => {
            const changes = this.changes;
            this.saving = changes;
            this.changes = {};
            this.options.onSave!(changes).then(errors => {
                this.saving = {};
                if (autosave) {
                    // clear any errors that are no longer present, but dont add new ones
                    for (const field in this.errors) {
                        if (!errors || !errors[field]) {
                            delete this.errors[field];
                        }
                    }
                } else {
                    this.errors = errors;
                }
                this.repaintAll().then(() => resolve(this.errors), () => reject);
            }, reject);
        });

        try {
            return await this.saveInProgress;
        } finally {
            this.saveInProgress = undefined;
        }
    }
}

interface ObservableFieldProps<T, K extends keyof T> extends ObserveProps<T, K> {
    tooltip?: string;
    title: string;
    message?: string;
    required?: boolean;
}

interface ObserveProps<T, K extends keyof T> {
    name: K | K[];
    form: ObservableForm<T>;
    render: () => React.ReactNode;
}

export class Observe<T, K extends keyof T> extends React.Component<ObserveProps<T, K>> {
    componentDidMount() {
        if (this.props.name instanceof Array) {
            this.props.name.forEach(n => this.props.form.listen(n, this.onUpdate));
        } else {
            this.props.form.listen(this.props.name, this.onUpdate);
        }
    }

    componentWillUnmount() {
        if (this.props.name instanceof Array) {
            this.props.name.forEach(n => this.props.form.unlisten(n, this.onUpdate));
        } else {
            this.props.form.unlisten(this.props.name, this.onUpdate);
        }
    }

    render() {
        return this.props.render();
    }

    onUpdate = () => new Promise<void>(resolve => this.forceUpdate(resolve));
}

export class ObservableField<T, K extends keyof T> extends React.Component<
    ObservableFieldProps<T, K>
> {
    field = () => {
        const { name, form, render, ...fieldProps } = this.props;
        const id = name instanceof Array ? name[0] : name;
        const error = name instanceof Array ? form.getError(name[0]) : form.getError(name);

        return (
            <Field id={`field-${id}`} error={error} {...fieldProps}>
                {render()}
            </Field>
        );
    };

    render() {
        return <Observe form={this.props.form} name={this.props.name} render={this.field} />;
    }
}
